import { ArcElement, ChartData, Chart as ChartJS, ChartOptions } from 'chart.js';
import { rgba } from 'polished';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { tokens } from '@treams/treams-ui';

import { ratio } from '../CycleAnalytics';

ChartJS.register(ArcElement);

const ProgressChartWrapper = styled(Box)`
	position: relative;
	width: 80px;
	height: 80px;
`;

const ProgressChartLabel = styled(Flex)`
	position: absolute;
	inset: 0;
	align-items: center;
	justify-content: center;
	font-family: Poppins, sans-serif;
	font-weight: 700;
	font-size: 22px;
	color: ${tokens.text.primary};
`;

const ChartLayer = styled(Box)`
	position: absolute;
	inset: 0;
`;

export function CycleProgressChart(props: { completed: number; started?: number; total: number }) {
	const theme = useTheme();
	const { i18n } = useTranslation();

	const completed = Math.max(props.completed, 0);
	const started = Math.max(props.started ?? 0, 0);
	const remaining = Math.max(props.total - completed - started, 0);

	const completedRatio = ratio(props.completed, props.total);

	// Continuous full-circle track that fills the rest of the circle.
	const trackData: ChartData<'doughnut'> = {
		datasets: [
			{
				data: [1],
				backgroundColor: [rgba(theme.colors.primary, 0.12)],
				borderWidth: 0,
			},
		],
	};

	// Completed and started arcs, drawn on top of the track with rounded
	// corners. All three segments share the same (absolute) unit so they add up
	// to the full circle. The remaining slice is transparent so the track shows
	// through.
	const progressData: ChartData<'doughnut'> = {
		datasets: [
			{
				data: [completed, started, remaining],
				backgroundColor: [theme.colors.primary, rgba(theme.colors.primary, 0.5), 'transparent'],
				borderWidth: 0,
				borderRadius: 0,
			},
		],
	};

	const options: ChartOptions<'doughnut'> = {
		cutout: '85%',
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: { enabled: false },
		},
	};

	const percentLabel = `${(completedRatio * 100).toLocaleString(i18n.language, {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})}%`;

	return (
		<ProgressChartWrapper>
			<ChartLayer>
				<Doughnut data={trackData} options={{ ...options, animation: false }} />
			</ChartLayer>
			<ChartLayer>
				<Doughnut data={progressData} options={options} />
			</ChartLayer>
			<ProgressChartLabel>{percentLabel}</ProgressChartLabel>
		</ProgressChartWrapper>
	);
}
